export const DEFAULT_CONFIG_RETRY_DELAYS_MS = [0, 750, 2_000];
export const DEFAULT_CONFIG_REQUEST_TIMEOUT_MS = 8_000;
const DAY = 24 * 60 * 60 * 1_000;
const abortError = () => Object.assign(new Error('Lastingen ble avbrutt.'), {name: 'AbortError', retriable: false});
function browserStorage(){try{return globalThis.localStorage;}catch{return null;}}

export async function loadStorefrontConfig({url,cacheKey,fetchImpl=globalThis.fetch,storage=browserStorage(),retryDelaysMs=DEFAULT_CONFIG_RETRY_DELAYS_MS,requestTimeoutMs=DEFAULT_CONFIG_REQUEST_TIMEOUT_MS,cacheMaxAgeMs=DAY,staleCacheMaxAgeMs=7*DAY,now=()=>Date.now(),sleep=ms=>new Promise(r=>setTimeout(r,ms)),signal}){
 let lastError=new Error('Kunne ikke laste designeren.'),attempts=0;
 for(const delay of retryDelaysMs){
  if(signal?.aborted)throw abortError();
  if(delay)await sleep(delay);
  if(signal?.aborted)throw abortError();
  attempts++;
  try{
   const payload=await readConfig(fetchImpl,url,requestTimeoutMs,signal);
   writeCache(storage,cacheKey,{savedAt:now(),payload});
   return {payload,source:attempts>1?'network_retry':'network',attempts};
  }catch(error){
   if(signal?.aborted)throw abortError();
   lastError=error instanceof Error?error:new Error(String(error));
   // A revoked/removed/invalid configuration must never be resurrected by cache.
   if(lastError.retriable===false)break;
   const cached=readCache(storage,cacheKey,now(),cacheMaxAgeMs);
   if(cached)return {payload:cached.payload,source:'cache_fallback',attempts,recoveredFrom:lastError};
  }
 }
 if(lastError.retriable!==false){
  const cached=readCache(storage,cacheKey,now(),staleCacheMaxAgeMs);
  if(cached)return {payload:cached.payload,source:'stale_cache_fallback',attempts,recoveredFrom:lastError};
 }
 lastError.attempts=attempts;throw lastError;
}

async function readConfig(fetchImpl,url,timeoutMs,signal){
 const controller=new AbortController();
 let timer,rejectAbort;
 const cancelled=new Promise((_,reject)=>{rejectAbort=reject;});
 const cancel=()=>{controller.abort();rejectAbort(abortError());};
 signal?.addEventListener('abort',cancel,{once:true});
 if(signal?.aborted)cancel();
 const deadline=new Promise((_,reject)=>{
  timer=setTimeout(()=>{controller.abort();reject(Object.assign(new Error('Designeren brukte for lang tid på å svare. Prøv igjen.'),{name:'TimeoutError',retriable:true}));},timeoutMs);
 });
 try{
  const operation=(async()=>{
   const response=await fetchImpl(url,{cache:'no-store',signal:controller.signal});
   if(!response.ok){
    const error=Object.assign(new Error(`Designeren svarte HTTP ${response.status}.`),{statusCode:response.status,retriable:[408,425,429].includes(response.status)||response.status>=500});
    // No dependency on an error body that may itself never arrive.
    throw error;
   }
   const payload=await response.json();
   if(!payload?.ok)throw Object.assign(new Error(payload?.error||'Konfigurasjonen er ugyldig.'),{retriable:false});
   return payload;
  })();
  // The deadline covers headers AND JSON body, even if an adapter ignores abort.
  return await Promise.race([operation,deadline,cancelled]);
 }finally{clearTimeout(timer);signal?.removeEventListener('abort',cancel);}
}

export function configFailureReason(error,online=true){
 if(!online)return 'offline';
 if(error?.statusCode)return `http_${error.statusCode}`;
 return ['AbortError','TimeoutError'].includes(error?.name)?'request_timeout':'request_failed_after_retry';
}
function writeCache(storage,key,value){if(storage&&key)try{storage.setItem(key,JSON.stringify(value));}catch{}}
function readCache(storage,key,now,maxAge){
 if(!storage||!key)return null;
 try{const c=JSON.parse(storage.getItem(key)||'null'),age=now-c?.savedAt;return c?.payload?.ok&&Number.isFinite(age)&&age>=0&&age<=maxAge?c:null;}catch{return null;}
}
